/**
 * 维修工单详情页
 * 支持查看详情、接修、报价、状态流转、发货等操作
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Spin, Steps, Modal, Form, InputNumber, Input, Select, message, Table, Divider } from 'antd';
import { PrinterOutlined, DollarOutlined, SendOutlined, CheckOutlined } from '@ant-design/icons';
import { repairApi } from '../../services/api';

const statusMap = {
  pending: '待接修', received: '已接修', quoting: '待付款', paid: '已付款',
  repairing: '维修中', to_ship: '待发货', shipped: '已发货', completed: '已完成', cancelled: '已取消',
};
const statusColors = {
  pending: 'default', received: 'blue', quoting: 'orange', paid: 'cyan',
  repairing: 'processing', to_ship: 'gold', shipped: 'green', completed: 'success', cancelled: 'error',
};

const stepMap = { pending: 0, received: 1, quoting: 1, paid: 2, repairing: 3, to_ship: 4, shipped: 5, completed: 6 };

// 支付方式中文映射（含线下收款新增枚举）
const payMethodText = {
  cash: '现金', scan: '当面扫码', transfer: '转账', bank: '银行转账', other: '其他',
  wechat_pay: '微信支付', alipay: '支付宝', allinpay: '通联支付', admin_web: '后台代付',
};

function RepairDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quoteModal, setQuoteModal] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [shipModal, setShipModal] = useState(false);
  const [payModal, setPayModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [form] = Form.useForm();
  const [payForm] = Form.useForm();

  const loadDetail = async () => {
    setLoading(true);
    try {
      const res = await repairApi.detail(id);
      setDetail(res.data);
    } catch (err) {
      message.error('加载详情失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDetail(); }, [id]);

  // 接修操作
  const handleReceive = async () => {
    try {
      const res = await repairApi.receive(id);
      message.success('接修成功');
      // 打开打印窗口
      if (res.data?.printHtml) {
        const win = window.open('', '_blank');
        win.document.write(res.data.printHtml);
        win.document.close();
      }
      loadDetail();
    } catch (err) { }
  };

  // 报价操作
  const handleQuote = async (values) => {
    try {
      await repairApi.quote(id, values);
      message.success('报价成功，已通知客户支付');
      setQuoteModal(false);
      loadDetail();
    } catch (err) { }
  };

  // 线下收款登记（上门收机/门店自取等线下场景，客户未在小程序在线支付）
  const handleOfflinePay = async (values) => {
    try {
      await repairApi.offlinePay(id, values);
      message.success('收款登记成功，工单已进入待维修');
      setPayModal(false);
      loadDetail();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || '收款登记失败，请重试';
      message.error(msg);
    }
  };

  // 状态变更
  const handleStatusChange = async (values) => {
    try {
      await repairApi.updateStatus(id, values);
      message.success('状态更新成功');
      setStatusModal(false);
      loadDetail();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || '状态更新失败，请重试';
      message.error(msg);
    }
  };

  // 发货（取消单即时"寄回"设备）
  const handleShip = async (values) => {
    try {
      await repairApi.ship(id, values);
      message.success(detail.status === 'cancelled' ? '寄回成功，设备已发回客户' : '发货成功');
      setShipModal(false);
      loadDetail();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || '发货失败，请重试';
      message.error(msg);
    }
  };

  // 退款（通联原路退回，仅管理员可见可操作）
  const handleRefund = async () => {
    setRefunding(true);
    try {
      const res = await repairApi.refund(id, refundReason);
      message.success(res.message || '退款成功');
      setRefundModal(false);
      setRefundReason('');
      loadDetail();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || '退款失败，请重试';
      message.error(msg);
    } finally {
      setRefunding(false);
    }
  };

  if (loading || !detail) return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;

  const step = stepMap[detail.status] || 0;

  // 退款按钮条件：后端给出可退预算 + 当前登录者具备 refund 权限（管理员；admin_info 由 App 定期刷新）
  const canRefund = (() => {
    if (!detail.refund?.canRefund) return false;
    try {
      const info = JSON.parse(localStorage.getItem('admin_info') || '{}');
      if (info.role === 'admin') return true;
      return (info.permissions || []).includes('repair:refund');
    } catch { return false; }
  })();

  return (
    <div>
      {/* 步骤条 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Steps current={step} size="small" status={detail.status === 'cancelled' ? 'error' : undefined}>
          <Steps.Step title="报修" />
          <Steps.Step title="接修" />
          <Steps.Step title="收款" />
          <Steps.Step title="维修" />
          <Steps.Step title="发货" />
          <Steps.Step title="完成" />
        </Steps>
      </Card>

      {/* 基本信息 */}
      <Card title={`维修单 ${detail.repair_no}`} extra={
        <Space>
          {detail.status === 'pending' && <Button type="primary" onClick={handleReceive} icon={<PrinterOutlined />}>接修并打印流转单</Button>}
          {(detail.status === 'received' || detail.status === 'quoting') && <Button type="primary" onClick={() => { form.resetFields(); setQuoteModal(true); }} icon={<DollarOutlined />}>报价</Button>}
          {detail.status === 'quoting' && detail.pay_status !== 'paid' &&
            <Button type="primary" style={{ background: '#52c41a', borderColor: '#52c41a' }}
              onClick={() => { payForm.resetFields(); payForm.setFieldsValue({ amount: parseFloat(detail.total_fee || 0), pay_method: 'cash' }); setPayModal(true); }}
              icon={<DollarOutlined />}>线下收款</Button>}
          {(detail.status === 'paid' || detail.status === 'repairing') && <Button onClick={() => { form.resetFields(); setStatusModal(true); }}>变更状态</Button>}
          {(detail.status === 'repairing' || detail.status === 'to_ship' || detail.status === 'cancelled') && <Button type="primary" onClick={() => { form.resetFields(); setShipModal(true); }} icon={detail.status === 'cancelled' ? <SendOutlined /> : <SendOutlined />}>{detail.status === 'cancelled' ? '寄回设备' : '发货'}</Button>}
          {canRefund && <Button danger type="primary" icon={<DollarOutlined />} onClick={() => { setRefundReason(''); setRefundModal(true); }}>退款 ¥{detail.refund.refundAmount.toFixed(2)}</Button>}
        </Space>
      }>
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="单号">{detail.repair_no}</Descriptions.Item>
          <Descriptions.Item label="状态"><Tag color={statusColors[detail.status]}>{statusMap[detail.status]}</Tag></Descriptions.Item>
          <Descriptions.Item label="客户姓名">{detail.customer_name}</Descriptions.Item>
          <Descriptions.Item label="手机号">{detail.phone}</Descriptions.Item>
          <Descriptions.Item label="联系人">{detail.contact_person}</Descriptions.Item>
          <Descriptions.Item label="SN/ID号">{detail.sn_number || '无'}</Descriptions.Item>
          <Descriptions.Item label="回寄地址" span={2}>{detail.return_address}</Descriptions.Item>
          <Descriptions.Item label="故障描述" span={2}>{detail.fault_desc}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 费用信息 */}
      <Card title="费用明细" size="small" style={{ marginTop: 16 }}>
        <Descriptions bordered column={4} size="small">
          <Descriptions.Item label="检测费">¥{parseFloat(detail.detection_fee || 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="材料费">¥{parseFloat(detail.material_fee || 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="邮费">¥{parseFloat(detail.postage || 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="合计"><span style={{ fontWeight: 'bold', color: '#f5222d' }}>¥{parseFloat(detail.total_fee || 0).toFixed(2)}</span></Descriptions.Item>
        </Descriptions>
        {detail.pay_status === 'refunded' && (
          <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 4 }}>
            <span style={{ color: '#cf1322', fontWeight: 600 }}>
              已退款：检测费 ¥{parseFloat(detail.detection_fee || 0).toFixed(2)} + 运费 ¥{parseFloat(detail.postage || 0).toFixed(2)} 由客户承担不退还，
              实退 ¥{(() => { const pr = detail.payRecords?.find(r => r.status === 'refunded'); return pr && pr.refund_amount != null ? parseFloat(pr.refund_amount).toFixed(2) : parseFloat(detail.total_fee || 0).toFixed(2); })()}
            </span>
            {(() => { const pr = detail.payRecords?.find(r => r.status === 'refunded'); return pr?.refund_time ? <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>（{new Date(pr.refund_time).toLocaleString('zh-CN')}）</span> : null; })()}
          </div>
        )}
      </Card>

      {/* 物流信息 */}
      {(detail.express_no || detail.customer_express_no) && (
        <Card title="物流信息" size="small" style={{ marginTop: 16 }}>
          <Descriptions bordered column={2} size="small">
            {detail.customer_express_no && <Descriptions.Item label="客户寄来快递">{detail.customer_express_company} {detail.customer_express_no}</Descriptions.Item>}
            {detail.express_no && <Descriptions.Item label="发回快递">{detail.express_company} {detail.express_no}</Descriptions.Item>}
          </Descriptions>
        </Card>
      )}

      {/* SN照片 */}
      {detail.sn_image && (
        <Card title="SN照片" size="small" style={{ marginTop: 16 }}>
          <img src={detail.sn_image} alt="SN照片" style={{ maxWidth: 400, maxHeight: 400 }} />
        </Card>
      )}

      {/* 支付记录 */}
      {detail.payRecords?.length > 0 && (
        <Card title="支付记录" size="small" style={{ marginTop: 16 }}>
          <Table rowKey="id" dataSource={detail.payRecords} columns={[
            { title: '交易号', dataIndex: 'transaction_id' },
            { title: '实付金额', dataIndex: 'amount', render: (v) => `¥${v}` },
            {
              title: '实退金额',
              dataIndex: 'refund_amount',
              render: (v, rec) => {
                if (rec.status !== 'refunded') return '-';
                return v != null ? <span style={{ color: '#f5222d', fontWeight: 600 }}>-¥{v}</span> : <Tag color="red">已退(金额未记录)</Tag>;
              },
            },
            { title: '支付方式', dataIndex: 'pay_method', render: (v) => payMethodText[v] || v },
            {
              title: '状态',
              dataIndex: 'status',
              render: (s) => {
                const map = { success: ['green', '已支付'], refunded: ['red', '已退款'], pending: ['orange', '待支付'], failed: ['default', '失败'] };
                const [color, text] = map[s] || ['default', s];
                return <Tag color={color}>{text}</Tag>;
              },
            },
            {
              title: '退款时间',
              dataIndex: 'refund_time',
              render: (v, rec) => {
                if (rec.status !== 'refunded' || !v) return '-';
                return new Date(v).toLocaleString('zh-CN');
              },
            },
            { title: '支付时间', dataIndex: 'created_at', render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
          ]} pagination={false} size="small" />
        </Card>
      )}

      {/* 报价弹窗 */}
      <Modal title="维修报价" open={quoteModal} onCancel={() => setQuoteModal(false)} onOk={() => form.submit()}>
        <Form form={form} layout="vertical" onFinish={handleQuote}>
          <Form.Item name="detection_fee" label="检测费（可抵维修费）" rules={[{ required: true }]}>
            <InputNumber prefix="¥" min={0} step={0.01} style={{ width: '100%' }} placeholder="请输入检测费用" />
          </Form.Item>
          <Form.Item name="material_fee" label="材料费">
            <InputNumber prefix="¥" min={0} step={0.01} style={{ width: '100%' }} placeholder="请输入材料费用" />
          </Form.Item>
          <Form.Item name="postage" label="邮费">
            <InputNumber prefix="¥" min={0} step={0.01} style={{ width: '100%' }} placeholder="请输入邮寄费用" />
          </Form.Item>
          <Form.Item name="material_detail" label="材料明细 (JSON数组)">
            <Input.TextArea rows={3} placeholder='[{"name":"屏幕","qty":1,"price":200}]' />
          </Form.Item>
        </Form>
      </Modal>

      {/* 线下收款登记弹窗 */}
      <Modal title="线下收款登记" open={payModal} onCancel={() => setPayModal(false)} onOk={() => payForm.submit()} okText="确认收款">
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, fontSize: 13, color: '#389e0d' }}>
          该工单报价合计 <b>¥{parseFloat(detail.total_fee || 0).toFixed(2)}</b>，客户未走小程序在线支付。确认当面/上门收款后登记入账，工单将进入"已付款 → 待维修"。
        </div>
        <Form form={payForm} layout="vertical" onFinish={handleOfflinePay}>
          <Form.Item
            name="amount"
            label="实收金额"
            rules={[{ required: true, message: '请输入实收金额' }]}
            extra="默认等于报价合计，如抹零可修改"
          >
            <InputNumber prefix="¥" min={0.01} precision={2} style={{ width: '100%' }} placeholder="请输入实收金额" />
          </Form.Item>
          <Form.Item name="pay_method" label="收款方式" rules={[{ required: true, message: '请选择收款方式' }]}>
            <Select
              placeholder="请选择收款方式"
              options={[
                { value: 'cash', label: '现金' },
                { value: 'scan', label: '当面扫码（微信/支付宝收款码）' },
                { value: 'transfer', label: '转账（微信/支付宝转账）' },
                { value: 'bank', label: '银行转账' },
                { value: 'other', label: '其他' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 状态变更弹窗 */}
      <Modal title="变更工单状态" open={statusModal} onCancel={() => setStatusModal(false)} onOk={() => form.submit()}>
        <Form form={form} layout="vertical" onFinish={handleStatusChange}>
          <Form.Item name="status" label="目标状态" rules={[{ required: true, message: '请选择目标状态' }]}>
            <Select
              placeholder="请选择目标状态"
              options={[
                { value: 'repairing', label: '维修中' },
                { value: 'to_ship', label: '待发货' },
                { value: 'completed', label: '已完成' },
                { value: 'cancelled', label: '已取消' },
              ]}
            />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 发货弹窗 */}
      <Modal title="发货" open={shipModal} onCancel={() => setShipModal(false)} onOk={() => form.submit()}>
        <Form form={form} layout="vertical" onFinish={handleShip}>
          <Form.Item name="express_company" label="快递公司" initialValue="京东快递">
            <Input />
          </Form.Item>
          <Form.Item name="express_no" label="快递单号（京东自动获取可不填）">
            <Input placeholder="系统将自动调用京东快递获取运单号" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 退款确认弹窗 */}
      <Modal
        title="通联原路退款"
        open={refundModal}
        onCancel={() => { setRefundModal(false); setRefundReason(''); }}
        onOk={handleRefund}
        okText="确认退款"
        okButtonProps={{ danger: true, loading: refunding }}
        cancelText="再想想"
        width={520}
      >
        {detail.refund && (
          <>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#888' }}>
              该维修单由客户通过通联(微信)支付 ¥{detail.refund.payAmount.toFixed(2)}，按取消维修结算规则：
            </div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="实付金额">¥{detail.refund.payAmount.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="实退金额">
                <span style={{ fontWeight: 'bold', color: '#f5222d' }}>¥{detail.refund.refundAmount.toFixed(2)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="扣除检测费">-¥{detail.refund.detectionFee.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="扣除运费">-¥{detail.refund.postage.toFixed(2)}</Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 10, padding: '6px 10px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, fontSize: 13, color: '#d46b08' }}>
              检测费与运费由客户承担不退还；实退金额将原路退回客户微信，确认后不可撤销。
            </div>
            <Input.TextArea
              rows={2}
              style={{ marginTop: 10 }}
              placeholder="退款原因（选填，将记录到工单备注）"
              value={refundReason}
              onChange={e => setRefundReason(e.target.value)}
            />
          </>
        )}
      </Modal>
    </div>
  );
}

export default RepairDetail;
